import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ClientService from "../../services/ClientService";
import $ from "jquery";
import "datatables.net";
import { Toast } from "primereact/toast";
import { SaPageShell, SaPageHeader, SaPrimaryButton, SA } from "../shared/SuperAdminUi";

/* ── helpers ───────────────────────────────────────────────────── */
const GRADIENTS = [
  "#5e72e4","#2dce89","#fb6340","#f5365c","#11cdef","#344767","#825ee4","#00875a",
];
const pickColor = (str = "") => {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return GRADIENTS[h % GRADIENTS.length];
};
const initials = (name = "") =>
  name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("") || "?";
const esc = (s) => String(s ?? "").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");

/* ── inline styles injected once ───────────────────────────────── */
const CSS = `
.cl-table-wrap { overflow-x: auto; }
.cl-table { width:100%; border-collapse:collapse; }
.cl-table thead tr { background:linear-gradient(135deg,#344767 0%,#21243d 100%); }
.cl-table thead th {
  padding:13px 14px; font-size:10px; font-weight:800; color:rgba(255,255,255,0.7);
  text-transform:uppercase; letter-spacing:.06em; white-space:nowrap;
  border:none;
}
.cl-table tbody tr { border-bottom:1px solid #f0f2f5; transition:background .15s; }
.cl-table tbody tr:hover { background:#f8f9fe; }
.cl-table tbody td { padding:11px 14px; font-size:13px; color:#344767; vertical-align:middle; }

/* agency cell */
.cl-agency { display:flex; align-items:center; gap:10px; }
.cl-avatar {
  width:36px; height:36px; border-radius:10px;
  display:flex; align-items:center; justify-content:center;
  font-size:13px; font-weight:800; color:#fff; flex-shrink:0;
}
.cl-agency-name { font-weight:700; font-size:13px; color:#344767; white-space:nowrap; }

/* location chip */
.cl-loc {
  display:inline-flex; align-items:center; gap:5px;
  background:#f0f2ff; color:#5e72e4; border-radius:20px;
  padding:3px 10px; font-size:11px; font-weight:700;
}

/* muted */
.cl-muted { color:#8392ab; }

/* tag pill */
.cl-tag {
  display:inline-flex; align-items:center; gap:4px;
  padding:3px 9px; border-radius:20px;
  font-size:11px; font-weight:700;
}
.cl-tag-gst  { background:#eef0ff; color:#5e72e4; }
.cl-tag-lic  { background:#e6faf3; color:#2dce89; }
.cl-tag-reg  { background:#fff2ee; color:#fb6340; }

/* edit button */
.cl-edit-btn {
  border:1.5px solid #e9ecef; background:#fff; border-radius:9px;
  width:34px; height:34px; cursor:pointer; color:#5e72e4;
  display:inline-flex; align-items:center; justify-content:center;
  font-size:13px; transition:background .15s, border-color .15s;
}
.cl-edit-btn:hover { background:#5e72e4; color:#fff; border-color:#5e72e4; }

/* DataTables overrides */
#cl-dt-wrapper .dataTables_wrapper { font-size:13px; color:#344767; }
#cl-dt-wrapper .dataTables_filter input {
  border:1.5px solid #e9ecef; border-radius:10px; padding:8px 14px;
  font-size:13px; outline:none; color:#344767;
}
#cl-dt-wrapper .dataTables_filter label { font-weight:600; color:#8392ab; }
#cl-dt-wrapper .dataTables_length select {
  border:1.5px solid #e9ecef; border-radius:8px; padding:5px 8px; font-size:13px;
}
#cl-dt-wrapper .dataTables_info { font-size:12px; color:#8392ab; }
#cl-dt-wrapper .paginate_button {
  border-radius:8px !important; font-size:13px !important; font-weight:600 !important;
  padding:6px 12px !important; margin:0 2px !important;
}
#cl-dt-wrapper .paginate_button.current {
  background:linear-gradient(135deg,#5e72e4,#825ee4) !important;
  border-color:transparent !important; color:#fff !important;
}
`;

function Clients() {
  const tableRef = useRef(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const toast = useRef(null);

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const data = await ClientService.getAllCLients();
      const list = Array.isArray(data) ? data : data?.$values ?? [];
      setClients(list);
    } catch {
      toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to fetch clients", life: 4000 });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  useEffect(() => {
    const el = tableRef.current;
    if (!el || clients.length === 0) return;

    const t = setTimeout(() => {
      if ($.fn.DataTable.isDataTable(el)) $(el).DataTable().destroy();

      $(el).DataTable({
        data: clients,
        pageLength: 25,
        order: [[0, "asc"]],
        language: { search: "", searchPlaceholder: "Search clients…" },
        columnDefs: [{ targets: -1, orderable: false }],
        drawCallback: function () {
          // wire edit buttons rendered in cells
          $(el).find(".cl-edit-btn").off("click").on("click", function () {
            const id = $(this).data("id");
            navigate(`/editClient/${id}`);
          });
        },
        columns: [
          {
            data: null,
            title: "Agency",
            render: (_, __, row) => {
              const name = row.agencyName || "Unnamed";
              const color = pickColor(name);
              return `<div class="cl-agency">
                <div class="cl-avatar" style="background:${color}">${esc(initials(name))}</div>
                <span class="cl-agency-name">${esc(name)}</span>
              </div>`;
            },
          },
          {
            data: "email",
            title: "Email",
            render: (d) => d
              ? `<a href="mailto:${esc(d)}" style="color:#5e72e4;text-decoration:none;font-weight:500">${esc(d)}</a>`
              : `<span class="cl-muted">—</span>`,
          },
          {
            data: "phoneNumber",
            title: "Phone",
            render: (d) => d
              ? `<a href="tel:${esc(d)}" style="color:#344767;text-decoration:none;font-weight:500">${esc(d)}</a>`
              : `<span class="cl-muted">—</span>`,
          },
          {
            data: null,
            title: "Location",
            render: (_, __, row) => {
              const loc = [row.city, row.state, row.country].filter(Boolean).join(", ");
              return loc
                ? `<span class="cl-loc"><i class="fas fa-map-marker-alt"></i>${esc(loc)}</span>`
                : `<span class="cl-muted">—</span>`;
            },
          },
          {
            data: null,
            title: "Credentials",
            render: (_, __, row) => {
              const tags = [];
              if (row.gstNumber)          tags.push(`<span class="cl-tag cl-tag-gst"><i class="fas fa-file-invoice"></i>GST</span>`);
              if (row.license)            tags.push(`<span class="cl-tag cl-tag-lic"><i class="fas fa-id-card"></i>Licensed</span>`);
              if (row.registrationNumber) tags.push(`<span class="cl-tag cl-tag-reg"><i class="fas fa-registered"></i>Registered</span>`);
              return tags.length ? `<div style="display:flex;gap:5px;flex-wrap:wrap">${tags.join("")}</div>` : `<span class="cl-muted">—</span>`;
            },
          },
          {
            data: "id",
            title: "",
            render: (id) =>
              `<button class="cl-edit-btn" data-id="${id}" title="Edit client"><i class="fas fa-pen"></i></button>`,
          },
        ],
      });
    }, 0);

    return () => {
      clearTimeout(t);
      if ($.fn.DataTable.isDataTable(el)) $(el).DataTable().destroy();
    };
  }, [clients, navigate]);

  return (
    <SaPageShell>
      <style>{CSS}</style>
      <Toast ref={toast} />

      <SaPageHeader
        title="Clients"
        subtitle="Manage agencies, subscriptions, and integrations from one place."
        actions={
          <SaPrimaryButton iconClass="fas fa-plus" onClick={() => navigate("/addClient")}>
            Add client
          </SaPrimaryButton>
        }
      />

      {/* stat bar */}
      {!loading && clients.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", marginBottom: "22px" }}>
          {[
            { label: "Total", value: clients.length, icon: "fa-building", color: SA.primary },
            { label: "With GST", value: clients.filter((c) => c.gstNumber).length, icon: "fa-file-invoice", color: "#2dce89" },
            { label: "Licensed", value: clients.filter((c) => c.license).length, icon: "fa-id-card", color: "#fb6340" },
            { label: "Countries", value: new Set(clients.map((c) => c.country).filter(Boolean)).size, icon: "fa-globe", color: "#11cdef" },
          ].map(({ label, value, icon, color }) => (
            <div key={label} style={{ display:"flex", alignItems:"center", gap:"10px", background:"#fff", border:`1px solid ${SA.border}`, borderRadius:"12px", padding:"12px 18px", boxShadow:"0 2px 8px rgba(52,71,103,0.06)" }}>
              <div style={{ width:"36px", height:"36px", borderRadius:"9px", background:`${color}18`, display:"flex", alignItems:"center", justifyContent:"center", color, fontSize:"15px" }}>
                <i className={`fas ${icon}`} />
              </div>
              <div>
                <p style={{ margin:0, fontSize:"18px", fontWeight:"800", color:SA.inkDeep, lineHeight:1 }}>{value}</p>
                <p style={{ margin:0, fontSize:"11px", color:SA.muted, fontWeight:"600", textTransform:"uppercase", letterSpacing:"0.04em" }}>{label}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* loading */}
      {loading && (
        <div style={{ textAlign:"center", padding:"80px 0", color:SA.muted }}>
          <i className="fas fa-spinner fa-spin" style={{ fontSize:"28px", color:SA.primary, display:"block", marginBottom:"12px" }} />
          Loading clients…
        </div>
      )}

      {/* empty */}
      {!loading && clients.length === 0 && (
        <div style={{ textAlign:"center", padding:"70px 24px", background:"#fff", borderRadius:"18px", border:`1px solid ${SA.border}` }}>
          <i className="fas fa-building" style={{ fontSize:"40px", color:`${SA.primary}40`, display:"block", marginBottom:"14px" }} />
          <p style={{ margin:"0 0 18px", color:SA.muted, fontSize:"15px" }}>No clients yet. Add your first agency.</p>
          <SaPrimaryButton iconClass="fas fa-plus" onClick={() => navigate("/addClient")}>Add client</SaPrimaryButton>
        </div>
      )}

      {/* table */}
      {!loading && clients.length > 0 && (
        <div id="cl-dt-wrapper" style={{ background:"#fff", borderRadius:"18px", boxShadow:"0 2px 24px rgba(52,71,103,0.09)", overflow:"hidden" }}>
          <div style={{ padding:"18px 20px 0", display:"flex", alignItems:"center", gap:"10px", borderBottom:`1px solid ${SA.border}` }}>
            <i className="fas fa-building" style={{ color:SA.primary, fontSize:"16px" }} />
            <span style={{ fontWeight:"800", fontSize:"15px", color:SA.ink }}>All Clients</span>
            <span style={{ marginLeft:"auto", background:`${SA.primary}12`, color:SA.primary, borderRadius:"20px", padding:"3px 12px", fontSize:"12px", fontWeight:"700" }}>
              {clients.length} total
            </span>
          </div>
          <div style={{ padding:"16px 20px 20px" }} className="cl-table-wrap">
            <table ref={tableRef} className="cl-table" style={{ width:"100%" }} />
          </div>
        </div>
      )}
    </SaPageShell>
  );
}

export default Clients;
